import type { ErrorBody, LoginResponse } from './types';

/** One failing field of a v1 request (`error.details[]`). */
export interface V1FieldError {
  field: string;
  message: string;
}

/**
 * A failed request. Legacy routes: `message` is the API's ErrorResponse.message (shown to the user
 * as is). v1 routes (/api/v1, docs/SAAS.md §4): `code` is the stable error code to translate and
 * `details` names the failing fields; `message` is the API's English text.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body: ErrorBody | null,
    readonly code: string | null = null,
    readonly details: V1FieldError[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** A v1 response: `{ success, data, message }` — or `{ success: false, error }`. */
interface V1Envelope {
  success: boolean;
  data?: unknown;
  error?: { code?: string; message?: string; details?: V1FieldError[] };
}

function isEnvelope(value: unknown): value is V1Envelope {
  return typeof value === 'object' && value !== null && typeof (value as { success?: unknown }).success === 'boolean' && ('data' in value || 'error' in value);
}

/** The payload of a response: a v1 envelope's `data`, a legacy body as it came. */
export function unwrap<T>(value: unknown): T {
  return (isEnvelope(value) ? value.data : value) as T;
}

export interface Session {
  accessToken: string;
  refreshToken: string;
}

/** Where the app keeps the session (localStorage in the browser, memory in tests). */
export interface SessionStore {
  get(): Session | null;
  set(session: Session | null): void;
}

export interface HttpOptions<L extends Session = LoginResponse> {
  /** '' for same origin (the dev proxy / one Nginx host), or e.g. https://gpsback.viganium.net */
  baseUrl: string;
  session: SessionStore;
  /** Where a refresh token is traded for new tokens: the legacy /api/auth/refresh by default, /api/v1/platform/auth/refresh for the platform. */
  refreshPath?: string;
  /**
   * Called when the API refuses a refresh: the session is gone, send the user to sign in. `reason`
   * is that refusal ("Your company account is suspended"). An API that cannot be reached, or fails
   * (a restart, a 502), ends nothing: the request fails with that error and the next one tries again.
   */
  onSessionExpired?: (reason: ApiError | null) => void;
  /** Called with every refreshed login (tokens + the current user). */
  onRefreshed?: (login: L) => void;
  fetch?: typeof fetch;
}

export type Query = Record<string, string | number | boolean | null | undefined | Array<string | number>>;

export interface RequestOptions {
  query?: Query;
  body?: unknown;
  form?: FormData;
  /** false for /api/auth/*: never attach or refresh a token */
  auth?: boolean;
  signal?: AbortSignal;
}

export interface Http {
  request<T>(method: string, path: string, options?: RequestOptions): Promise<T>;
  /** For downloads: the raw Response of a successful request. */
  raw(method: string, path: string, options?: RequestOptions): Promise<Response>;
  url(path: string, query?: Query): string;
}

export function buildQuery(query: Query | undefined): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue;
    if (Array.isArray(value)) value.forEach((v) => params.append(key, String(v)));
    else params.append(key, String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

/**
 * fetch with the legacy API's auth rules. An expired or invalid access token comes back as
 * 401 — or, from the legacy security chain, as a **403 with an empty body** (a role failure is
 * a 403 WITH an ErrorResponse). Either way the session is refreshed once (one refresh shared by
 * every request in flight) and the request retried. A v1 response's envelope (docs/SAAS.md §4)
 * is unwrapped: callers get its `data`.
 */
export function createHttp<L extends Session = LoginResponse>(options: HttpOptions<L>): Http {
  const doFetch = options.fetch ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init));
  /** ok: new tokens · otherwise `error` is why the request cannot go on (null: the session ended). */
  type Refreshed = { ok: true } | { ok: false; error: unknown };
  let refreshing: Promise<Refreshed> | null = null;

  const url = (path: string, query?: Query) => `${options.baseUrl}${path}${buildQuery(query)}`;

  async function refresh(): Promise<Refreshed> {
    const current = options.session.get();
    if (!current) return { ok: false, error: null };
    try {
      const res = await doFetch(url(options.refreshPath ?? '/api/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      });
      if (!res.ok) throw await toError(res);
      const login = unwrap<L>(await res.json());
      options.session.set({ accessToken: login.accessToken, refreshToken: login.refreshToken });
      options.onRefreshed?.(login);
      return { ok: true };
    } catch (error) {
      // the API said no (a dead refresh token, a suspended company): the session is over
      if (error instanceof ApiError && error.status < 500) {
        options.session.set(null);
        options.onSessionExpired?.(error);
        return { ok: false, error: null };
      }
      // the API was away or failing (a restart, a deploy): keep the session, and say why this request failed
      return { ok: false, error };
    }
  }

  function refreshOnce(): Promise<Refreshed> {
    refreshing ??= refresh().finally(() => {
      refreshing = null;
    });
    return refreshing;
  }

  async function send(method: string, path: string, opts: RequestOptions, retried: boolean): Promise<Response> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    let body: BodyInit | undefined;
    if (opts.form) body = opts.form;
    else if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }
    const auth = opts.auth !== false;
    const session = auth ? options.session.get() : null;
    if (session) headers.Authorization = `Bearer ${session.accessToken}`;

    const res = await doFetch(url(path, opts.query), { method, headers, body, signal: opts.signal });

    if (auth && session && !retried && (res.status === 401 || (res.status === 403 && (await isEmpty(res))))) {
      const refreshed = await refreshOnce();
      if (refreshed.ok) return send(method, path, opts, true);
      if (refreshed.error) throw refreshed.error;
    }
    if (!res.ok) throw await toError(res);
    return res;
  }

  return {
    url,
    raw: (method, path, opts = {}) => send(method, path, opts, false),
    async request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
      const res = await send(method, path, opts, false);
      const text = await res.text();
      if (!text) return undefined as T;
      const type = res.headers.get('content-type') ?? '';
      return type.includes('json') ? unwrap<T>(JSON.parse(text)) : (text as T);
    },
  };
}

async function isEmpty(res: Response): Promise<boolean> {
  return (await res.clone().text()).trim() === '';
}

async function toError(res: Response): Promise<ApiError> {
  const text = await res.text().catch(() => '');
  let body: ErrorBody | null = null;
  try {
    body = text ? (JSON.parse(text) as ErrorBody & { error?: string }) : null;
  } catch {
    body = null;
  }
  if (isEnvelope(body) && body.error) {
    const e = body.error;
    return new ApiError(res.status, e.message ?? `${res.status} ${res.statusText}`.trim(), null, e.code ?? null, e.details ?? []);
  }
  const message = body?.message ?? (body as { error?: string } | null)?.error ?? (res.status === 403 ? 'Access denied' : `${res.status} ${res.statusText}`.trim());
  return new ApiError(res.status, message, body);
}
